import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store/index'
import { beforeEachGuard } from '@/router/guard/index'
import { isManagerGuard, isAnyManagerGuard, profileEditGuard } from './guard/permission'

import BaseLayout from '@/layouts/base.vue'
import EmptyLayout from '@/layouts/empty.vue'
import BeforeLoginLayout from '@/layouts/before_login.vue'
import AfterLoginLayout from '@/layouts/after_login.vue'
import EventsPageLayout from '@/layouts/events_page_layout.vue'
import MemberSelectLayout from '@/layouts/member_select_layout.vue'

import Login from '@/pages/auth/login.vue'

import PasswordReminder from '@/pages/auth/password_reminder.vue'
import PasswordSet from '@/pages/auth/password_set.vue'

import Members from '@/pages/members/index.vue'
import GoalList from '@/pages/members/goal_list.vue'
import GoalAdd from '@/pages/members/goal_add.vue'
import GoalEdit from '@/pages/members/goal_edit.vue'
import ActivityResultAdd from '@/pages/members/activity_result_add.vue'
import ActivityResultEdit from '@/pages/members/activity_result_edit.vue'
import MandalaEvaluation from '@/pages/members/mandala_evaluation/index.vue'
import Profile from '@/pages/members/profile/index.vue'
import ProfileEmergencyContacts from '@/pages/members/profile/emergency_contacts/index.vue'
import ProfilePastDepartments from '@/pages/members/profile/past_departments/index.vue'
import ProfilePathSurveysEdit from '@/pages/members/profile/path_surveys/edit.vue'
import ProfilePathActivitiesAdd from '@/pages/members/profile/path_activities/add.vue'
import ProfilePathActivitiesEdit from '@/pages/members/profile/path_activities/edit.vue'
import ProfileFinalDestination from '@/pages/members/profile/final_destination/index.vue'
import ProfileAcademicRecordsAdd from '@/pages/members/profile/academic_records/add.vue'
import ProfileAcademicRecordsEdit from '@/pages/members/profile/academic_records/edit.vue'
import ProfileBodyMeasurementsEdit from '@/pages/members/profile/body_measurements/edit.vue'
import ProfileBodyMeasurementsAdd from '@/pages/members/profile/body_measurements/add.vue'

import Notifications from '@/pages/notifications.vue'
import Search from '@/pages/search.vue'
import Settings from '@/pages/settings.vue'
import SettingsPasswordUpdate from '@/pages/settings/password_update.vue'
import SettingsEmailUpdate from '@/pages/settings/email_update.vue'
import SettingsFollow from '@/pages/settings/follow.vue'

import Events from '@/pages/events/index.vue'
import EventAdd from '@/pages/events/add.vue'
import EventEdit from '@/pages/events/edit.vue'

import AdminMembers from '@/pages/admin/members.vue'
import AdminMemberAdd from '@/pages/admin/member_add.vue'
import AdminMemberEdit from '@/pages/admin/member_edit.vue'
import AdminGroups from '@/pages/admin/groups.vue'
import AdminGroupAdd from '@/pages/admin/group_add.vue'
import AdminGroupEdit from '@/pages/admin/group_edit.vue'
import AdminCategories from '@/pages/admin/categories.vue'
import AdminCategoryAdd from '@/pages/admin/category_add.vue'
import AdminCategoryEdit from '@/pages/admin/category_edit.vue'
import AdminTemplates from '@/pages/admin/templates.vue'
import AdminTemplateAdd from '@/pages/admin/template_add.vue'
import AdminTemplateEdit from '@/pages/admin/template_edit.vue'
import MandalaGoalTemplates from '@/pages/admin/mandala_goal_templates/index.vue'
import MandalaGoalTemplateAdd from '@/pages/admin/mandala_goal_templates/add.vue'
import MandalaGoalTemplateEdit from '@/pages/admin/mandala_goal_templates/edit.vue'
import TemplateManagement from '@/pages/admin/template_management.vue'
import NoteTemplates from '@/pages/admin/note_templates/index.vue'
import NoteTemplateAdd from '@/pages/admin/note_templates/add.vue'
import NoteTemplateEdit from '@/pages/admin/note_templates/edit.vue'

import Records from '@/pages/admin/records/index.vue'
import RecordsProfileSelect from '@/pages/admin/records/profile/index.vue'
import RecordsNoteRecords from '@/pages/admin/records/profile/note_records.vue'
import RecordsBodyMeasurements from '@/pages/admin/records/profile/body_measurements.vue'
import RecordsPathSurveys from '@/pages/admin/records/profile/path_surveys.vue'
import RecordsMandalaGoalSelect from '@/pages/admin/records/mandala_goal/index.vue'
import RecordsMandalaGoalList from '@/pages/admin/records/mandala_goal/list.vue'
import RecordsAcademicRecords from '@/pages/admin/records/profile/academic_records.vue'

import Maintenance from '@/pages/maintenance.vue'
import { redirectIfNotAccessEvents } from './guard/redirectIfNotAccessEvents'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    component: BaseLayout,
    children: [
      {
        path: '',
        meta: { isLoginRequired: true },
      },
      {
        path: 'maintenance',
        component: Maintenance,
      },
      {
        path: 'auth/',
        component: BeforeLoginLayout,
        children: [
          {
            path: 'login',
            component: Login,
            beforeEnter: async (to, from, next) => {
              const isExpired = await store.dispatch('auth/tokenIsExpired')
              if (isExpired) {
                await store.dispatch('auth/logout')
              }
              next()
            }
          },
          {
            path: 'password_reminder',
            component: PasswordReminder
          },
          {
            path: 'password_set',
            component: PasswordSet,
          }
        ],
      },
      {
        path: 'members/',
        component: AfterLoginLayout,
        meta: { isLoginRequired: true },
        children: [
          {
            path: '',
            name: 'members',
            component: Members,
          },
          {
            path: ':id/',
            component: MemberSelectLayout,
            children: [
              {
                path: '',
                name: 'goalList',
                component: GoalList,
              },
              {
                name: 'mandalaEvaluation',
                path: 'mandala_evaluation',
                component: MandalaEvaluation,
              },
              {
                path: 'goal_add',
                name: 'goalAdd',
                component: GoalAdd,
              },
              {
                path: 'goal_edit/:goalId',
                name: 'goalEdit',
                component: GoalEdit,
              },
              {
                path: 'goal/:goalId/activity_result_add',
                name: 'activityResultAdd',
                component: ActivityResultAdd,
              },
              {
                path: 'goal/:goalId/activity_result_edit/:activityId',
                name: 'activityResultEdit',
                component: ActivityResultEdit,
              },
              {
                path: 'profile',
                component: EmptyLayout,
                children: [
                  {
                    path: '',
                    component: Profile,
                  },
                  {
                    path: 'emergency_contacts',
                    component: ProfileEmergencyContacts,
                    beforeEnter: profileEditGuard
                  },
                  {
                    path: 'past_departments',
                    component: ProfilePastDepartments,
                    beforeEnter: profileEditGuard
                  },
                  {
                    path: 'path_surveys/',
                    component: EmptyLayout,
                    children: [
                      {
                        path: 'edit',
                        component: ProfilePathSurveysEdit,
                      },
                    ],
                    beforeEnter: profileEditGuard
                  },
                  {
                    path: 'path_activities/',
                    component: EmptyLayout,
                    children: [
                      {
                        path: 'add',
                        component: ProfilePathActivitiesAdd,
                      },
                      {
                        path: ':pathActivityId',
                        component: ProfilePathActivitiesEdit,
                      },
                    ],
                    beforeEnter: profileEditGuard
                  },
                  {
                    path: 'final_destination',
                    component: ProfileFinalDestination,
                  },
                  {
                    path: 'academic_records',
                    component: EmptyLayout,
                    children: [
                      {
                        path: 'add',
                        component: ProfileAcademicRecordsAdd,
                      },
                      {
                        path: ':academicRecordId',
                        component: ProfileAcademicRecordsEdit,
                      },
                    ],
                    beforeEnter: profileEditGuard
                  },
                  {
                    path: 'body_measurements',
                    component: EmptyLayout,
                    children: [
                      {
                        path: 'add',
                        component: ProfileBodyMeasurementsAdd,
                      },
                      {
                        path: ':bodyMeasurementId',
                        component: ProfileBodyMeasurementsEdit,
                      },
                    ],
                    beforeEnter: profileEditGuard
                  }
                ]
              }
            ]
          },
        ]
      },
      {
        path: 'events/',
        component: AfterLoginLayout,
        meta: {isLoginRequired: true},
        beforeEnter: redirectIfNotAccessEvents(),
        children: [
          {
            path: '',
            component: EventsPageLayout,
            children: [
              {
                path: '',
                name: 'events',
                component: Events
              }
            ]
          },
          {
            path: 'add',
            name: 'eventAdd',
            component: EventAdd,
            beforeEnter: isAnyManagerGuard('/events'),
          },
          {
            path: ':id/edit',
            name: 'eventEdit',
            component: EventEdit,
            beforeEnter: isAnyManagerGuard('/events'),
          },
        ]
      },
      {
        path: '/notifications/',
        component: AfterLoginLayout,
        meta: { isLoginRequired: true },
        children: [
          {
            path: '',
            component: Notifications,
          },
        ]
      },
      {
        path: '/search/',
        component: AfterLoginLayout,
        meta: { isLoginRequired: true },

        children: [
          {
            path: '',
            component: Search,
          },
        ]
      },
      {
        path: '/settings/',
        component: AfterLoginLayout,
        meta: { isLoginRequired: true },
        children: [
          {
            name: 'settings',
            path: '',
            component: Settings,
          },
          {
            name: 'passwordUpdate',
            path: 'password_update',
            component: SettingsPasswordUpdate,
          },
          {
            name: 'emailUpdate',
            path: 'email_update',
            component: SettingsEmailUpdate,
          },
          {
            name: 'follow',
            path: 'follow',
            component: SettingsFollow,
          },
        ]
      },
      {
        path: '/admin/',
        component: AfterLoginLayout,
        children: [
          {
            path: 'members/',
            component: EmptyLayout,
            beforeEnter: isManagerGuard('/'),
            children: [
              {
                path: '',
                component: AdminMembers,
              },
              {
                path: 'add',
                component: AdminMemberAdd,
              },
              {
                path: ':id/edit',
                name: 'adminMembersMemberEdit',
                component: AdminMemberEdit,
              },
            ]
          },
          {
            path: 'groups/',
            component: EmptyLayout,
            beforeEnter: isManagerGuard('/'),
            children: [
              {
                path: '',
                component: AdminGroups,
              },
              {
                path: 'add',
                component: AdminGroupAdd,
              },
              {
                path: ':groupId/edit',
                component: AdminGroupEdit,
              },
            ]
          },
          {
            path: 'categories/',
            component: EmptyLayout,
            beforeEnter: isAnyManagerGuard('/'),
            children: [
              {
                path: '',
                component: AdminCategories,
              },
              {
                path: 'add',
                component: AdminCategoryAdd,
              },
              {
                path: ':categoryId/edit',
                component: AdminCategoryEdit,
              },
            ]
          },
          {
            path: 'templates/',
            component: EmptyLayout,
            beforeEnter: isAnyManagerGuard('/'),
            children: [
              {
                path: '',
                component: AdminTemplates,
              },
              {
                path: 'add',
                component: AdminTemplateAdd,
              },
              {
                path: ':templateId/edit',
                component: AdminTemplateEdit,
              },
            ]
          },
          {
            path: 'template_management/',
            component: TemplateManagement,
            beforeEnter: isAnyManagerGuard('/'),
          },
          {
            path: 'note_templates/',
            component: EmptyLayout,
            beforeEnter: isAnyManagerGuard('/'),
            children: [
              {
                path: '',
                component: NoteTemplates,
              },
              {
                path: 'add',
                component: NoteTemplateAdd,
              },
              {
                path: ':noteTemplateId/edit',
                component: NoteTemplateEdit,
              },
            ]
          },
          {
            path: 'mandala_goal_templates/',
            component: EmptyLayout,
            beforeEnter: isAnyManagerGuard('/'),
            children: [
              {
                path: '',
                component: MandalaGoalTemplates,
              },
              {
                path: 'add',
                component: MandalaGoalTemplateAdd,
              },
              {
                path: ':id',
                component: MandalaGoalTemplateEdit,
              },
            ]
          },
          {
            path: 'records/',
            component: EmptyLayout,
            beforeEnter: isAnyManagerGuard('/'),
            children: [
              {
                path: '',
                name: 'recordList',
                component: Records,
              },
              {
                path: 'profile/',
                name: 'recordListProfileSelect',
                component: RecordsProfileSelect,
                beforeEnter: isManagerGuard('/'),
                children: [
                  {
                    path: 'note_records',
                    name: 'recordListNoteRecords',
                    component: RecordsNoteRecords,
                  },
                  {
                    path: 'body_measurements',
                    name: 'recordListBodyMeasurements',
                    component: RecordsBodyMeasurements,
                  },
                  {
                    path: 'path_surveys',
                    name: 'recordListPathSurveys',
                    component: RecordsPathSurveys,
                  },
                  {
                    path: 'academic_records',
                    name: 'recordListAcademicRecords',
                    component: RecordsAcademicRecords,
                  },
                ]
              },
              {
                path: 'mandala_goal/',
                component: EmptyLayout,
                children: [
                  {
                    path: '',
                    name: 'recordListMandalaGoalSelect',
                    component: RecordsMandalaGoalSelect,
                    children: [
                      {
                        path: 'list',
                        name: 'recordListMandalaGoalList',
                        component: RecordsMandalaGoalList,
                      },
                    ]
                  },
                ]
              },
            ]
          },
        ]
      },
    ],
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(beforeEachGuard)

router.afterEach(() => {
  // 画面遷移時は画面上部に強制スクロール
  window.scrollTo(0, 0)
})

export default router
